import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    RadioGroup,
    Radio,
    Box,
    Typography,
    Divider,
    TextField,
    Chip,
    Stack,
    LinearProgress,
    Fade,
    IconButton,
} from '@mui/material';
import {
    Download as DownloadIcon,
    Close as CloseIcon,
    TableChart as TableChartIcon,
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import * as XLSX from '../../utils/secureXlsx';
import { saveAs } from 'file-saver';
import { Worker } from '../../models/worker';
import { format } from 'date-fns';

interface WorkerExportModalProps {
    open: boolean;
    onClose: () => void;
    workers: Worker[];
    totalCount: number;
    onExportAll?: () => Promise<Worker[]>;
}

type ExportFormat = 'excel' | 'csv';
type ExportScope = 'current' | 'all';

interface ExportColumns {
    basicInfo: boolean;
    contactInfo: boolean;
    professionalInfo: boolean;
    statusInfo: boolean;
    managementInfo: boolean;
    timestamps: boolean;
}

const WorkerExportModal: React.FC<WorkerExportModalProps> = ({
    open,
    onClose,
    workers,
    totalCount,
    onExportAll,
}) => {
    const [exportFormat, setExportFormat] = useState<ExportFormat>('excel');
    const [exportScope, setExportScope] = useState<ExportScope>('current');
    const [fileName, setFileName] = useState(`workers_export_${format(new Date(), 'yyyy-MM-dd')}`);
    const [isExporting, setIsExporting] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);
    const [columns, setColumns] = useState<ExportColumns>({
        basicInfo: true,
        contactInfo: true,
        professionalInfo: true,
        statusInfo: true,
        managementInfo: false,
        timestamps: false,
    });

    const handleColumnToggle = (column: keyof ExportColumns) => {
        setColumns(prev => ({ ...prev, [column]: !prev[column] }));
    };

    const handleSelectAll = () => {
        const allSelected = Object.values(columns).every(v => v);
        setColumns({
            basicInfo: !allSelected,
            contactInfo: !allSelected,
            professionalInfo: !allSelected,
            statusInfo: !allSelected,
            managementInfo: !allSelected,
            timestamps: !allSelected,
        });
    };

    const getSelectedColumnsCount = () => {
        return Object.values(columns).filter(v => v).length;
    };

    const formatWorkerData = (worker: Worker) => {
        const data: any = {};

        if (columns.basicInfo) {
            data['Worker ID'] = worker.workerId;
            data['Full Name'] = worker.fullName;
            data['Date of Birth'] = worker.dateOfBirth ? format(new Date(worker.dateOfBirth), 'yyyy-MM-dd') : '';
            data['Nationality'] = worker.nationality || '';
            data['Language'] = worker.languages?.[0] || '';
        }

        if (columns.contactInfo) {
            data['Email'] = worker.emailAddress || '';
            data['Phone'] = worker.phoneNumber || '';
        }

        if (columns.professionalInfo) {
            data['Profession'] = worker.professionKey || '';
            // Certifications now live on a separate table; not included in this export. Use the cert review queue for cert listings.
            data['Certifications'] = '';
        }

        if (columns.statusInfo) {
            data['Status'] = worker.status?.replace(/_/g, ' ') || '';
            data['Availability'] = worker.workerAvailability || '';
        }

        if (columns.managementInfo) {
            data['Recruiter'] = worker.recruiter ? 
                `${worker.recruiter.firstName || ''} ${worker.recruiter.lastName || ''}`.trim() || worker.recruiter.username : '';
            data['Account Manager'] = worker.accountManager ? 
                `${worker.accountManager.firstName || ''} ${worker.accountManager.lastName || ''}`.trim() || worker.accountManager.username : '';
        }

        if (columns.timestamps) {
            data['Created At'] = worker.createdAt ? format(new Date(worker.createdAt), 'yyyy-MM-dd HH:mm') : '';
            data['Updated At'] = worker.updatedAt ? format(new Date(worker.updatedAt), 'yyyy-MM-dd HH:mm') : '';
        }

        return data;
    };

    const handleExport = async () => {
        setIsExporting(true);
        setExportSuccess(false);

        try {
            let dataToExport: Worker[] = workers;

            // If exporting all and callback provided, fetch all data
            if (exportScope === 'all' && onExportAll) {
                dataToExport = await onExportAll();
            }

            // Format data for export
            const formattedData = dataToExport.map(formatWorkerData);

            if (exportFormat === 'excel') {
                // Create workbook
                const ws = XLSX.utils.json_to_sheet(formattedData);
                const wb = XLSX.utils.book_new();
                
                // Auto-size columns
                const maxWidths: number[] = [];
                const headers = Object.keys(formattedData[0] || {});
                
                headers.forEach((header, i) => {
                    maxWidths[i] = header.length;
                    formattedData.forEach(row => {
                        const cellValue = String(row[header] || '');
                        if (cellValue.length > maxWidths[i]) {
                            maxWidths[i] = cellValue.length;
                        }
                    });
                });

                ws['!cols'] = maxWidths.map(w => ({ wch: Math.min(w + 2, 50) }));

                // Add styling to header row
                const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const address = XLSX.utils.encode_col(C) + '1';
                    if (!ws[address]) continue;
                    ws[address].s = {
                        font: { bold: true },
                        fill: { fgColor: { rgb: 'F59F0C' } },
                        alignment: { horizontal: 'center' },
                    };
                }

                XLSX.utils.book_append_sheet(wb, ws, 'Workers');
                
                // Add summary sheet
                const summaryData = [
                    { 'Export Information': 'Date', 'Value': format(new Date(), 'yyyy-MM-dd HH:mm:ss') },
                    { 'Export Information': 'Total Records', 'Value': dataToExport.length },
                    { 'Export Information': 'Export Type', 'Value': exportScope === 'all' ? 'All Workers' : 'Current Page' },
                    { 'Export Information': '', 'Value': '' },
                    { 'Export Information': 'Status Summary', 'Value': '' },
                ];

                // Calculate status counts
                const statusCounts: Record<string, number> = {};
                dataToExport.forEach(worker => {
                    const status = worker.status || 'Unknown';
                    statusCounts[status] = (statusCounts[status] || 0) + 1;
                });

                Object.entries(statusCounts).forEach(([status, count]) => {
                    summaryData.push({
                        'Export Information': status.replace(/_/g, ' '),
                        'Value': String(count),
                    });
                });

                const summaryWs = XLSX.utils.json_to_sheet(summaryData);
                XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

                // Generate Excel file
                const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
                const buf = new ArrayBuffer(wbout.length);
                const view = new Uint8Array(buf);
                for (let i = 0; i < wbout.length; i++) {
                    view[i] = wbout.charCodeAt(i) & 0xFF;
                }
                const blob = new Blob([buf], { type: XLSX.XLSX_MIME_TYPE });
                saveAs(blob, `${fileName}.xlsx`);
            } else {
                // CSV export
                const ws = XLSX.utils.json_to_sheet(formattedData);
                const csv = XLSX.utils.sheet_to_csv(ws);
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                saveAs(blob, `${fileName}.csv`);
            }

            setExportSuccess(true);
            setTimeout(() => {
                onClose();
                setExportSuccess(false);
            }, 2000);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    background: theme => theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DownloadIcon color="primary" />
                    <Typography variant="h6">Export Workers</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pb: 1 }}>
                {exportSuccess ? (
                    <Fade in={exportSuccess}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 4,
                            gap: 2,
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
                            <Typography variant="h6" color="success.main">
                                Export Successful!
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Your file has been downloaded
                            </Typography>
                        </Box>
                    </Fade>
                ) : (
                    <Stack spacing={3}>
                        {/* Export Scope */}
                        <Box>
                            <FormControl component="fieldset">
                                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                                    Export Scope
                                </FormLabel>
                                <RadioGroup
                                    value={exportScope}
                                    onChange={(e) => setExportScope(e.target.value as ExportScope)}
                                >
                                    <FormControlLabel 
                                        value="current" 
                                        control={<Radio />} 
                                        label={
                                            <Box>
                                                <Typography variant="body2">Current View</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Export {workers.length} visible workers
                                                </Typography>
                                            </Box>
                                        } 
                                    />
                                    <FormControlLabel 
                                        value="all" 
                                        control={<Radio />} 
                                        disabled={!onExportAll}
                                        label={
                                            <Box>
                                                <Typography variant="body2">All Workers</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Export all {totalCount} workers matching current filters
                                                </Typography>
                                            </Box>
                                        } 
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        <Divider />

                        {/* Export Format */}
                        <Box>
                            <FormControl component="fieldset">
                                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                                    File Format
                                </FormLabel>
                                <Stack direction="row" spacing={2}>
                                    <Chip
                                        icon={<TableChartIcon />}
                                        label="Excel (.xlsx)"
                                        onClick={() => setExportFormat('excel')}
                                        color={exportFormat === 'excel' ? 'primary' : 'default'}
                                        variant={exportFormat === 'excel' ? 'filled' : 'outlined'}
                                        sx={{ py: 2.5, fontSize: '0.9rem' }}
                                    />
                                    <Chip
                                        icon={<DescriptionIcon />}
                                        label="CSV (.csv)"
                                        onClick={() => setExportFormat('csv')}
                                        color={exportFormat === 'csv' ? 'primary' : 'default'}
                                        variant={exportFormat === 'csv' ? 'filled' : 'outlined'}
                                        sx={{ py: 2.5, fontSize: '0.9rem' }}
                                    />
                                </Stack>
                            </FormControl>
                        </Box>

                        <Divider />

                        {/* Column Selection */}
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                    Data Columns ({getSelectedColumnsCount()} selected)
                                </FormLabel>
                                <Button size="small" onClick={handleSelectAll}>
                                    {getSelectedColumnsCount() === 6 ? 'Deselect All' : 'Select All'}
                                </Button>
                            </Box>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={columns.basicInfo} onChange={() => handleColumnToggle('basicInfo')} />}
                                    label="Basic Information (ID, Name, DOB, Nationality, Language)"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={columns.contactInfo} onChange={() => handleColumnToggle('contactInfo')} />}
                                    label="Contact Information (Email, Phone)"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={columns.professionalInfo} onChange={() => handleColumnToggle('professionalInfo')} />}
                                    label="Professional Information (Profession, Certifications)"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={columns.statusInfo} onChange={() => handleColumnToggle('statusInfo')} />}
                                    label="Status Information (Status, Availability)"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={columns.managementInfo} onChange={() => handleColumnToggle('managementInfo')} />}
                                    label="Management Information (Recruiter, Account Manager)"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={columns.timestamps} onChange={() => handleColumnToggle('timestamps')} />}
                                    label="Timestamps (Created At, Updated At)"
                                />
                            </FormGroup>
                        </Box>

                        <Divider />

                        {/* File Name */}
                        <TextField
                            fullWidth
                            label="File Name"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            size="small"
                            helperText={`Will be saved as ${fileName}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`}
                        />

                        {isExporting && (
                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Preparing export...
                                </Typography>
                                <LinearProgress />
                            </Box>
                        )}
                    </Stack>
                )}
            </DialogContent>

            {!exportSuccess && (
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleExport} 
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        disabled={isExporting || getSelectedColumnsCount() === 0}
                    >
                        {isExporting ? 'Exporting...' : `Export ${exportScope === 'all' ? 'All' : workers.length} Workers`}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default WorkerExportModal;
